import type { SequenceDetailStep, TemplateTokenContext } from "@unbox/api";
import {
  BellRinging01,
  Check,
  CheckCircle,
  Clock,
  CurrencyDollarCircle,
  Edit05,
  LinkBroken01,
  MessageChatSquare,
  Play,
  SearchLg,
  UserX01,
  XCircle,
  XClose,
} from "@untitledui/icons";
import { type ComponentProps, type ReactElement, type ReactNode, useState } from "react";
import { Focusable } from "react-aria-components";
import { useMessageTemplates } from "@/api/message-templates";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { AuthoriseTikTokAdsButton } from "@/components/safety-config/authorise-tiktok-ads-button";
import { cx } from "@/utils/cx";
import { pluralize } from "@/utils/text";
import { toTitleCase } from "@/utils/toTitleCase";
import { AutomatedMessageDialog } from "./automated-message-dialog";
import { NotifyTiktokStepCard } from "./notify-tiktok-step-card";

// Verbatim copy for a step that can't run without TikTok Ads authorised.
const GMV_MAX_STEP_BLOCKED_NOTE =
  "Want to include removing from GMV Max as an option? Authenticate the GMV Max APIs via the integrations page.";

export const SKIPPED_STEP_NOTE = "This stage will be skipped and not enforced";

// Delay options offered by the inline editor (1–21 days). The delay row only
// renders for a non-zero wait, so 0 isn't selectable — it would make the row
// disappear with no way to bring it back.
const DELAY_OPTIONS = Array.from({ length: 21 }, (_, i) => i + 1);

export const TRIGGER_LABELS: Record<string, string> = {
  safety_action: "Safety Action",
  collaboration_invite: "Follow up on invites",
  sample_delivered: "Chase received samples",
  creator_dormant: "Re-engage dormant creators",
  first_collab_video: "Welcome first collab videos",
};

/**
 * Event-phrased trigger labels used inside the sequence editor's trigger card,
 * describing *what happens* to start the sequence.
 */
export const TRIGGER_EVENT_LABELS: Record<string, string> = {
  safety_action: "Safety Action Taken",
  collaboration_invite: "Creator sent a collaboration invite",
  sample_delivered: "Creator receives sample (marked as delivered)",
  creator_dormant: "High-performing creator goes inactive for x days",
  first_collab_video: "Creator posts their first video for a collaboration",
};

export const TYPE_LABELS: Record<string, string> = {
  safety: "Safety Sequence",
  creator: "Creator Sequence",
};

export const TRIGGER_SCENARIOS: Record<string, string[]> = {
  safety: ["Reviewer triggers a safety action"],
  creator: [
    "Creator invited to collaboration",
    "Creator becomes dormant in collaboration",
    "Creator receives a sample",
  ],
};

/**
 * Exit conditions that cause a sequence run to stop early, keyed by trigger type.
 * Kept in sync with the worker's checkExitCondition (packages/worker/src/activities/sequences.ts).
 */
export const EXIT_SCENARIOS: Record<string, string[]> = {
  safety_action: ["Creator sends a message"],
  collaboration_invite: ["Creator sends a message", "Creator accepts collaboration invite", "Collaboration expires"],
  sample_delivered: ["Creator posts a video", "Creator sends a message", "Collaboration expires"],
  creator_dormant: ["Creator posts a video", "Creator sends a message", "Collaboration expires"],
  first_collab_video: ["Collaboration expires", "Creator removed from collaboration"],
};

// A branch step evaluates a condition and forks into a "taken"/"not_taken" arm.
// These describe the check itself and label each arm, keyed by branch condition.
export const BRANCH_CHECK_LABELS: Record<string, { title: string; description: string }> = {
  video_removed: {
    title: "Check whether video has been removed",
    description: "Unbox AI checks whether the video is still live on the creator's account",
  },
  metadata_edited: {
    title: "Check whether metadata has been edited",
    description: "Unbox AI re-scans the video to see if the creator updated the caption",
  },
};

export const BRANCH_ARM_LABELS: Record<string, { taken: string; notTaken: string }> = {
  video_removed: { taken: "If removed", notTaken: "If still live" },
  metadata_edited: { taken: "If edited", notTaken: "If not edited" },
};

/**
 * Compact single-line summary of a sequence's steps, for row-level previews.
 * Example: "Send Message → Wait 2d → Send Custom Message → Remove from Collaboration"
 */
export function summarizeSteps(
  steps: Array<{
    type: string;
    delayDays: number;
    messageTemplateId: string | null;
    customMessage: string | null;
  }>,
): string {
  const parts: string[] = [];
  for (const s of steps) {
    if (s.delayDays > 0) parts.push(`Wait ${s.delayDays}d`);
    parts.push(stepLabel(s));
  }
  return parts.join(" → ");
}

// One-line helper text describing what each action node does, shown beneath the
// step label. Keyed by step type; types without an entry (e.g. send_message,
// branch) render their own dedicated content instead.
export const STEP_HELPER_TEXT: Record<string, string> = {
  remove_from_gmv_max: "Excludes the video from GMV Max campaigns",
  add_to_gmv_max_campaign: "Adds the video to GMV Max campaigns",
  remove_from_linked_collab: "Removes creator from the linked collaboration",
  remove_from_collab: "Removes creator from all collaborations",
  block_creator: "Adds the creator to the Unbox blocklist",
  notify_tiktok: "Files a demonetisation request to TikTok for the video",
  resolve_risk: "Logs resolution · closes the risk",
};

// Trunk step types that show a numbered badge (1, 2, 3…) instead of their icon.
// resolve_risk stays an icon because it marks the sequence's outcome rather than an
// automated action; send_message stays an icon because it reads as a detail attached
// to the preceding action rather than a step of its own. Only applies to the trunk —
// branch/arm steps always keep their icon (see the comment above the trunk map).
const NUMBERED_TRUNK_STEP_TYPES = [
  "remove_from_collab",
  "remove_from_linked_collab",
  "remove_from_gmv_max",
  "add_to_gmv_max_campaign",
  "block_creator",
  "notify_tiktok",
];

export function stepLabel(step: { type: string; messageTemplateId: string | null; customMessage: string | null }) {
  if (step.type === "wait") return "Wait";
  if (step.type === "send_message")
    return step.customMessage && !step.messageTemplateId ? "Send Custom Message" : "Send Message";
  if (step.type === "remove_from_collab") return "Remove from All Collaborations";
  if (step.type === "remove_from_linked_collab") return "Remove from Linked Collaboration";
  if (step.type === "remove_from_gmv_max") return "Remove from GMV Max";
  if (step.type === "add_to_gmv_max_campaign") return "Add to GMV Max";
  if (step.type === "block_creator") return "Block Creator";
  if (step.type === "notify_tiktok") return "Notify TikTok";
  if (step.type === "branch") return "Check condition";
  if (step.type === "resolve_risk") return "Risk Resolved";
  return step.type;
}

export function StepGlyph({ type, className }: { type: string; className?: string }) {
  if (type === "send_message") return <MessageChatSquare className={className} />;
  if (type === "remove_from_collab" || type === "remove_from_linked_collab")
    return <LinkBroken01 className={className} />;
  if (type === "remove_from_gmv_max" || type === "add_to_gmv_max_campaign")
    return <CurrencyDollarCircle className={className} />;
  if (type === "block_creator") return <UserX01 className={className} />;
  if (type === "notify_tiktok") return <BellRinging01 className={className} />;
  if (type === "branch") return <SearchLg className={className} />;
  if (type === "resolve_risk") return <CheckCircle className={className} />;
  return <XCircle className={className} />;
}

// Section header shown above a group of steps (Trigger / Start policy automation /
// Monitor / If <branch> / End) — a label plus a horizontal rule filling the rest
// of the row, matching the individual policy Figma frame.
function SectionDivider({ label, className }: { label: string; className?: string }) {
  return (
    <div className={cx("relative z-10 flex items-center gap-2 h-5 w-full bg-white", className)}>
      <p className="text-sm font-medium text-quaternary whitespace-nowrap">{label}</p>
      <div className="flex-1 h-px bg-border-secondary" />
    </div>
  );
}

function formatSendAtTime(step: { sendAtHour: number | null }) {
  if (step.sendAtHour === null) return null;
  const period = step.sendAtHour >= 12 ? "PM" : "AM";
  const hour = step.sendAtHour % 12 || 12;
  return `${hour}:00 ${period}`;
}

export type Step = {
  step: number;
  type: string;
  delayDays: number;
  sendAtHour: number | null;
  sendAtTimezone: string | null;
  messageTemplateId: string | null;
  customMessage: string | null;
  branch?: "taken" | "not_taken" | null;
  branchCondition?: string | null;
  enabled?: boolean;
  /** True when this step performs a GMV Max action. */
  requiresGmvMax?: boolean;
} & Partial<Pick<SequenceDetailStep, "imageId" | "imageUrl">>;

// Patch emitted by the editable rows back to the owner of the step list.
export type StepPatch = Partial<
  Pick<
    Step,
    | "delayDays"
    | "messageTemplateId"
    | "customMessage"
    | "imageId"
    | "imageUrl"
    | "sendAtHour"
    | "sendAtTimezone"
    | "enabled"
  >
>;

type BranchableStep = {
  step: number;
  type: string;
  branch?: "taken" | "not_taken" | null;
  branchCondition?: string | null;
};

// Splits a sequence's flat step list into the trunk (steps that always run) and,
// when the sequence forks, the branch check step plus its two mutually-exclusive
// arms — shared by SequenceStepsPreview and any other consumer of `useSequence`
// that needs to render steps without flattening the branch into one fake sequence.
export function splitSequenceSteps<T extends BranchableStep>(steps: T[]) {
  const sorted = [...steps].sort((a, b) => a.step - b.step);
  const branchStep = sorted.find((s) => s.type === "branch");
  const trunk = sorted.filter((s) => s.type !== "branch" && (s.branch ?? null) === null);
  const takenArm = sorted.filter((s) => s.branch === "taken");
  const notTakenArm = sorted.filter((s) => s.branch === "not_taken");
  const armLabels = branchStep ? BRANCH_ARM_LABELS[branchStep.branchCondition ?? ""] : undefined;
  const check = branchStep ? BRANCH_CHECK_LABELS[branchStep.branchCondition ?? ""] : undefined;
  return { sorted, branchStep, trunk, takenArm, notTakenArm, armLabels, check };
}

/** Why editing is unavailable, shown on the controls it disables. */
export type EditDisabledReason = { title: string; description?: string };

// Keeps a gated control visible but disabled behind a tooltip explaining why,
// instead of hiding it — a hidden control reads as a missing feature or a bug
// rather than a permission restriction (UNB-2129). No reason: render as-is.
//
// `Focusable` is what lets the tooltip find its anchor: react-aria hands the
// trigger ref down through context, and only a react-aria component claims it. A
// plain element would leave that ref empty and the tooltip would render pinned to
// the viewport corner. Pass react-aria components (e.g. Toggle) straight to
// `Tooltip` instead — they claim the ref themselves.
function Lockable({
  reason,
  children,
}: {
  reason?: EditDisabledReason;
  children: ComponentProps<typeof Focusable>["children"];
}) {
  if (!reason) return children;
  return (
    <Tooltip title={reason.title} description={reason.description}>
      <Focusable isDisabled>{children}</Focusable>
    </Tooltip>
  );
}

/** {@link Lockable} for a react-aria child, which anchors the tooltip on its own. */
export function LockableToggle({ reason, children }: { reason?: EditDisabledReason; children: ReactElement }) {
  if (!reason) return children;
  return (
    <Tooltip title={reason.title} description={reason.description}>
      {children}
    </Tooltip>
  );
}

// A step's leading delay: a select while editing, otherwise a clickable "Wait N
// days" pill. Only rendered for non-zero waits, so days >= 1. Without `onChange`
// the pill stays visible but disabled when a `disabledReason` explains why, and
// degrades to plain text when the row is simply not editable.
function DelayRow({
  days,
  onChange,
  disabledReason,
}: {
  days: number;
  onChange?: (days: number) => void;
  disabledReason?: EditDisabledReason;
}) {
  const [editing, setEditing] = useState(false);

  return (
    <div className="flex items-center gap-3 py-6">
      <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full border border-secondary bg-secondary_subtle text-tertiary shrink-0">
        <Clock className="w-3.5 h-3.5" />
      </div>
      <div className="flex items-center gap-2 flex-1 rounded-lg border border-secondary bg-unbox-cream-25 px-4 py-2">
        {editing && onChange ? (
          <>
            <span className="text-sm font-medium text-tertiary">Wait</span>
            <select
              // biome-ignore lint/a11y/noAutofocus: focus the select when the row enters edit mode
              autoFocus
              value={days}
              onChange={(e) => {
                onChange(Number(e.target.value));
                setEditing(false);
              }}
              onBlur={() => setEditing(false)}
              className="rounded-full border border-secondary bg-primary px-2 py-0.5 text-sm font-semibold text-primary focus:outline-none focus:ring-2 focus:ring-brand/80"
            >
              {DELAY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <span className="text-sm font-medium text-tertiary">{days === 1 ? "day" : "days"}</span>
          </>
        ) : onChange || disabledReason ? (
          <Lockable reason={onChange ? undefined : disabledReason}>
            <button
              type="button"
              disabled={!onChange}
              onClick={() => setEditing(true)}
              className={cx(
                "flex items-center gap-1.5 text-sm font-medium text-tertiary",
                onChange ? "cursor-pointer hover:text-secondary" : "cursor-not-allowed",
              )}
              aria-label="Edit delay"
            >
              Wait {pluralize(days, "day", "days")}
              <Edit05 className="w-3.5 h-3.5" />
            </button>
          </Lockable>
        ) : (
          <span className="text-sm font-medium text-tertiary">Wait {pluralize(days, "day", "days")}</span>
        )}
      </div>
    </div>
  );
}

// The shared delay row + step card. `badge` is whatever sits on the spine (an
// icon for the step type, or the branch glyph). `title`/`subtitle` override the
// default label (used for branch checks). When `editable`, the delay row and
// send_message content become editable and emit patches via `onStepChange`.
function StepRow({
  step,
  badge,
  templates,
  title,
  subtitle,
  editable = false,
  onStepChange,
  variableContext,
  merchantResources,
  disabledReason,
  adsBlocked,
  larkBlocked,
  additionalToggleableStepTypes = [],
}: {
  step: Step;
  badge: ReactNode;
  templates: { id: string; name: string; content: string }[];
  title?: string;
  subtitle?: string;
  editable?: boolean;
  onStepChange?: (stepNumber: number, patch: StepPatch) => void;
  variableContext?: TemplateTokenContext;
  merchantResources?: boolean;
  disabledReason?: EditDisabledReason;
  /** True when TikTok Ads isn't authorised — combines with `step.requiresGmvMax` below. */
  adsBlocked?: boolean;
  larkBlocked?: boolean;
  additionalToggleableStepTypes?: readonly string[];
}) {
  const [messageOpen, setMessageOpen] = useState(false);
  // Only render a delay row where a wait actually exists (e.g. the branch's
  // "wait N days, then check"); immediate steps render none. When editable, the
  // shown delay becomes an inline editor.
  const showDelay = step.delayDays > 0;
  const canEditDelay = editable && !!onStepChange;
  // Fall back to the step type's helper text when no explicit subtitle is given.
  const resolvedSubtitle = subtitle ?? STEP_HELPER_TEXT[step.type];
  const isMessage = step.type === "send_message";
  const editableMessage = editable && isMessage && onStepChange;
  const hasMessageContent = step.messageTemplateId || step.customMessage;
  const isToggleStep = step.type === "notify_tiktok" || additionalToggleableStepTypes.includes(step.type);
  const canToggle = editable && !!onStepChange && isToggleStep;
  const isDisabled = step.enabled === false;
  const blocked = !!adsBlocked && !!step.requiresGmvMax;
  const skipped = isDisabled && !blocked;

  return (
    <div>
      {showDelay && (
        <DelayRow
          days={step.delayDays}
          onChange={canEditDelay && onStepChange ? (d) => onStepChange(step.step, { delayDays: d }) : undefined}
          disabledReason={disabledReason}
        />
      )}

      {step.type === "notify_tiktok" ? (
        <NotifyTiktokStepCard
          step={step}
          badge={badge}
          title={title}
          subtitle={resolvedSubtitle}
          canToggle={canToggle}
          disabledReason={disabledReason}
          larkBlocked={larkBlocked}
          onToggle={(enabled) => onStepChange?.(step.step, { enabled })}
          className={cx(!showDelay && "pt-4")}
        />
      ) : (
        <div className={cx("flex items-start gap-3", !showDelay && "pt-4")}>
          <div
            className={cx(
              "sequence-badge flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium shrink-0 mt-2.5",
              blocked ? "bg-error-50 border border-error-100 text-error-500" : "bg-secondary-solid text-white",
            )}
          >
            {blocked ? <XClose className="w-[19px] h-[19px]" /> : badge}
          </div>
          <div
            className={cx(
              "flex flex-col flex-1 rounded-lg border overflow-hidden",
              blocked && "border-error border-dashed bg-error-25",
              skipped && "border-primary border-dashed bg-primary",
              !blocked && !skipped && "border-secondary bg-unbox-cream-50",
            )}
          >
            <div className="flex items-center gap-2.5 px-4 py-3">
              <StepGlyph
                type={step.type}
                className={cx("w-5 h-5", skipped ? "text-fg-error-primary" : "text-tertiary")}
              />
              <span className="text-sm font-medium text-secondary flex-1">{title ?? stepLabel(step)}</span>
              {formatSendAtTime(step) && !isDisabled && (
                <span className="text-xs text-tertiary">
                  <Clock className="w-3 h-3 inline-block mr-1 -mt-px" />
                  {formatSendAtTime(step)}
                </span>
              )}
              {isMessage && !isDisabled && (editableMessage || disabledReason) && (
                <Lockable reason={editableMessage ? undefined : disabledReason}>
                  <button
                    type="button"
                    disabled={!editableMessage}
                    onClick={editableMessage ? () => setMessageOpen(true) : undefined}
                    aria-label="Edit message"
                    className={cx(
                      "flex items-center justify-center w-6 h-6 rounded p-1.5 text-tertiary",
                      editableMessage ? "hover:text-secondary cursor-pointer" : "cursor-not-allowed",
                    )}
                  >
                    <Edit05 className="w-4 h-4" />
                  </button>
                </Lockable>
              )}
              {isToggleStep && (canToggle || disabledReason) && (
                <LockableToggle reason={canToggle ? undefined : disabledReason}>
                  <Toggle
                    isSelected={!isDisabled}
                    isDisabled={!canToggle}
                    onChange={(v) => onStepChange?.(step.step, { enabled: v })}
                    aria-label={`${stepLabel(step)} ${isDisabled ? "disabled" : "enabled"}`}
                  />
                </LockableToggle>
              )}
            </div>
            {skipped ? (
              <div className="px-4 pb-3 text-sm text-error-primary">{SKIPPED_STEP_NOTE}</div>
            ) : (
              resolvedSubtitle && <div className="px-4 pb-3 text-sm text-tertiary">{resolvedSubtitle}</div>
            )}
            {blocked && (
              <div className="border-t border-error-200 bg-error-25 px-4 py-4 flex flex-col items-start gap-3">
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-medium text-quaternary">Not available</p>
                  <p className="text-sm text-tertiary font-medium">{GMV_MAX_STEP_BLOCKED_NOTE}</p>
                </div>
                <AuthoriseTikTokAdsButton />
              </div>
            )}
            {!blocked &&
              isMessage &&
              (hasMessageContent ? (
                // The message body is the largest edit target on the card, so it needs
                // the explanation as much as the pencil button does (UNB-2129).
                <Lockable reason={editableMessage ? undefined : disabledReason}>
                  <button
                    type="button"
                    disabled={!editableMessage}
                    onClick={editableMessage ? () => setMessageOpen(true) : undefined}
                    className={cx(
                      "border-t border-secondary bg-unbox-cream-25 px-4 py-3 text-left w-full",
                      editableMessage
                        ? "cursor-pointer hover:bg-secondary transition-colors"
                        : disabledReason && "cursor-not-allowed",
                    )}
                  >
                    {step.messageTemplateId ? (
                      <p className="text-xs text-tertiary mb-1">
                        Template: {templates.find((t) => t.id === step.messageTemplateId)?.name ?? "Unknown"}
                      </p>
                    ) : (
                      <p className="text-xs text-tertiary mb-1">Custom Message</p>
                    )}
                    <p className="text-sm text-secondary whitespace-pre-wrap break-words">
                      {step.messageTemplateId
                        ? (templates.find((t) => t.id === step.messageTemplateId)?.content ?? "")
                        : step.customMessage}
                    </p>
                  </button>
                </Lockable>
              ) : editableMessage || disabledReason ? (
                <Lockable reason={editableMessage ? undefined : disabledReason}>
                  <button
                    type="button"
                    disabled={!editableMessage}
                    onClick={editableMessage ? () => setMessageOpen(true) : undefined}
                    className={cx(
                      "border-t border-secondary bg-unbox-cream-25 px-4 py-3 text-left w-full",
                      editableMessage ? "cursor-pointer hover:bg-secondary transition-colors" : "cursor-not-allowed",
                    )}
                  >
                    <p className="text-sm italic text-utility-warning-600">
                      {editableMessage ? "Empty Message — click to edit" : "Empty Message"}
                    </p>
                  </button>
                </Lockable>
              ) : null)}
          </div>
        </div>
      )}

      {editableMessage && (
        <AutomatedMessageDialog
          open={messageOpen}
          onOpenChange={setMessageOpen}
          initialValue={{
            messageTemplateId: step.messageTemplateId,
            customMessage: step.customMessage,
            imageId: step.imageId ?? null,
            imageUrl: step.imageUrl ?? null,
            sendAtHour: step.sendAtHour,
            sendAtTimezone: step.sendAtTimezone,
          }}
          submitLabel="Save"
          variableContext={variableContext}
          merchantResources={merchantResources}
          onSubmit={(value) => onStepChange(step.step, value)}
        />
      )}
    </div>
  );
}

interface SequenceStepsPreviewProps {
  type: string;
  steps: Step[];
  className?: string;
  // Override the trigger and end cards (e.g. for the safety policy view).
  triggerTitle?: string;
  triggerSubtitle?: string;
  endLabel?: string;
  // When true, real (persisted) steps expose inline editing of their delay and
  // send_message content; edits are emitted via `onStepChange`. The synthetic
  // "Risk Resolved" terminal node is never editable.
  editable?: boolean;
  onStepChange?: (stepNumber: number, patch: StepPatch) => void;
  // Why editing is unavailable. When set, read-only steps keep their controls
  // visible but disabled behind this tooltip instead of hiding them (UNB-2129).
  disabledReason?: EditDisabledReason;
  // True when TikTok Ads isn't authorised. Never true for a workspace template,
  // which has no merchant to check authorisation for.
  adsBlocked?: boolean;
  // True when the merchant has no active Lark webhook. Never true for a workspace template,
  // which has no merchant to check.
  larkBlocked?: boolean;
  additionalToggleableStepTypes?: readonly string[];
  merchantResources?: boolean;
}

export function SequenceStepsPreview({
  type,
  steps,
  className,
  triggerTitle,
  triggerSubtitle,
  endLabel,
  editable = false,
  onStepChange,
  disabledReason,
  adsBlocked,
  larkBlocked,
  additionalToggleableStepTypes,
  merchantResources = true,
}: SequenceStepsPreviewProps) {
  const { data: templates = [] } = useMessageTemplates();

  // Safety sequences surface risk-related message tokens; everything else uses
  // the creator outreach set.
  const variableContext: TemplateTokenContext = type === "safety" ? "safety" : "creator-profile";

  const { trunk, branchStep, takenArm, notTakenArm, armLabels, check } = splitSequenceSteps(steps);

  // `resolve_risk` is now a real step type, so terminal "Risk Resolved" nodes render
  // as ordinary steps within the arm rather than being synthesized here.
  const renderArm = (label: string, armSteps: Step[]) => (
    <>
      <SectionDivider label={label} className="mt-8 mb-3" />
      {armSteps.map((step) => (
        <StepRow
          key={step.step}
          step={step}
          templates={templates}
          badge={<StepGlyph type={step.type} className="w-3.5 h-3.5" />}
          editable={editable}
          onStepChange={onStepChange}
          variableContext={variableContext}
          merchantResources={merchantResources}
          disabledReason={disabledReason}
          adsBlocked={adsBlocked}
          larkBlocked={larkBlocked}
          additionalToggleableStepTypes={additionalToggleableStepTypes}
        />
      ))}
    </>
  );

  return (
    <div className={`rounded-lg border border-border-secondary bg-white p-6 ${className ?? ""}`}>
      <div className="sequence-steps relative flex flex-col w-full max-w-xl">
        <SectionDivider label="Trigger" className="mb-3" />
        {/* Trigger row */}
        <div className="flex items-start gap-3">
          <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full bg-secondary-solid text-white shrink-0 mt-2.5">
            <Play className="w-3.5 h-3.5" />
          </div>
          <div className="flex flex-col flex-1 rounded-lg border border-secondary bg-unbox-cream-50 px-4 py-3">
            <span className="text-sm font-medium text-secondary">
              {triggerTitle ?? TYPE_LABELS[type] ?? toTitleCase(type)}
            </span>
            {triggerSubtitle && <span className="text-xs text-tertiary mt-0.5">{triggerSubtitle}</span>}
          </div>
        </div>

        {/* Trunk steps — numbered (1, 2, 3…) for automation actions per
            NUMBERED_TRUNK_STEP_TYPES, falling back to the step-type glyph otherwise.
            Branch/arm steps below always keep their glyph — a linear count can't span
            the two mutually-exclusive arms. */}
        {trunk.length > 0 && (
          <>
            <SectionDivider label="Start policy automation" className="mt-8 mb-3" />
            {(() => {
              let numberedCount = 0;
              return trunk.map((step) => {
                // A blocked step won't run, so it's skipped from the count — steps after it
                // are numbered as if it weren't there.
                const stepBlocked = !!adsBlocked && !!step.requiresGmvMax;
                const isNumbered = NUMBERED_TRUNK_STEP_TYPES.includes(step.type) && !stepBlocked;
                if (isNumbered) numberedCount += 1;
                return (
                  <StepRow
                    key={step.step}
                    step={step}
                    templates={templates}
                    badge={isNumbered ? numberedCount : <StepGlyph type={step.type} className="w-3.5 h-3.5" />}
                    editable={editable}
                    onStepChange={onStepChange}
                    variableContext={variableContext}
                    merchantResources={merchantResources}
                    disabledReason={disabledReason}
                    adsBlocked={adsBlocked}
                    larkBlocked={larkBlocked}
                    additionalToggleableStepTypes={additionalToggleableStepTypes}
                  />
                );
              });
            })()}
          </>
        )}

        {/* Branch: the check, then each arm. The branch step's leading delay
            ("wait N days, then check") is editable; the check itself is not. */}
        {branchStep && (
          <>
            <SectionDivider label="Monitor" className="mt-8 mb-3" />
            <StepRow
              step={branchStep}
              templates={templates}
              badge={<SearchLg className="w-3.5 h-3.5" />}
              title={check?.title ?? "Check condition"}
              subtitle={check?.description}
              editable={editable}
              onStepChange={onStepChange}
              disabledReason={disabledReason}
              adsBlocked={adsBlocked}
              larkBlocked={larkBlocked}
            />
            {renderArm(armLabels?.taken ?? "If true", takenArm)}
            {renderArm(armLabels?.notTaken ?? "If false", notTakenArm)}
          </>
        )}

        {/* End of sequence */}
        <SectionDivider label="End" className="mt-8 mb-3" />
        <div className="flex items-center gap-3">
          <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full bg-secondary-solid text-white shrink-0">
            <Check className="w-3.5 h-3.5" />
          </div>
          <div className="flex items-center flex-1 rounded-lg border border-secondary bg-unbox-cream-50 px-4 py-3">
            <span className="text-sm font-medium text-tertiary flex-1">{endLabel ?? "End of Sequence"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
